import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bMerchantService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-merchant-service'
import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useFpB2bPaymentTransactionPolling } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-transaction-polling'
import { useFpB2bShippingService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-shipping-service'
import { useHandleCheckoutException, type UseHandleCheckoutExceptionArgs } from '@cart-services/hooks/useHandleCheckoutException'
import { getCouponExceptionMessage } from '@cart-services/utils/errorHandlers'
import { DIALOG_COUPON_EXCEPTION, DialogCouponException } from '@cart-ui/DialogCouponException/DialogCouponException'
import { DIALOG_REDIRECT_CONFIRM, DialogRedirectConfirm, type DialogRedirectConfirmProps } from '@cart-ui/DialogRedirectConfirm/DialogRedirectConfirm'
import { DIALOG_VALIDATE_STOCK, DialogValidateStock, type DialogValidateStockProps } from '@cart-ui/DialogValidateStock/DialogValidateStock'
import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { CountryCode, DATE_FORMAT, DeliveryTimeSlot, PaymentMethod, Platform } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { DialogPayMeQRCode } from '@dialog-qr-code/DialogPayMeQRCode'
import { useGa4ClientId } from '@dni-analysis'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { type ResponseErrorGenericsV2 } from '@dni-types/utilities/response'
import { useApiBlockoutDeliveryDays } from '@hooks-api/fortune/use-api-blockout-delivery-days'
import { useApiCampaignActivityInfoImmutable } from '@hooks-api/fortune/useApiCampaignActivityInfoImmutable'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiFpCretePaymentTransaction } from '@hooks-api/fortune/useApiFpCretePaymentTransaction'
import { useApiGetMerchant } from '@hooks-api/fortune-fastpass/use-api-get-merchant'
import { useApiMerchantProfile } from '@hooks-api/fortune-fastpass/useApiMerchantProfile'
import { useApiPartnerProfile } from '@hooks-api/fortune-fastpass/useApiPartnerProfile'
import { AppliedStatus, StockExceptionCode, type CartValidationDetail, type CouponExceptionCodeType } from '@hooks-api/types/dataModel/coupon'
import { type ReceiptType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { type PaymentMethod as PaymentMethodTypeECommerce, PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { DELIVERY_CLASS } from '@hooks-api/types/dataModel/shipping'
import { type Nullable } from '@hooks-api/types/nullable'
import { getCkoPaymentErrorInfo, CheckoutPayAlipayHkModule, CheckoutPayApplePayModule, CheckoutPayCreditCardModule } from '@payments'
import { isPayMeUrl } from '@payments/lib/is-payme-url'
import { PaymentError } from '@payments/lib/PaymentError'
import { Box } from '@pets-ui/Box/Box'
import { Container } from '@pets-ui/Container/Container'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { resolveInvoiceDetailFields } from '@utils/invoice-detail-fields'
import { type AxiosError } from 'axios'
import { format, fromUnixTime } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom-v5'
import { PaymentProvider, useContextPayment } from '../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { CKO_ERROR_MESSAGE } from '../../fp-b2b-shared/constants/cko-error-message'
import { useCheckout } from '../../fp-b2b-shared/hooks/use-checkout'
import { useFpB2bPaymentDispatch } from '../../fp-b2b-shared/hooks/use-fp-b2b-payment-dispatch'
import { usePreOrderDeliveryDate } from '../../fp-b2b-shared/hooks/use-pre-order-delivery-date'
import { getErrorContent } from '../../fp-b2b-shared/utils/get-error-content'
import { CartFooterDetail } from '../components/PageLayout/CartFooterDetail'
import { PageFooter } from '../components/PageLayout/PageFooter'
import { CheckoutFooterAction } from './components/CheckoutFooterAction'
import { DeliveryNotes } from './components/DeliveryNotes/DeliveryNotes'
import { DIALOG_CHECKOUT_NOTIFY, DialogCheckoutNotify } from './components/DialogCheckoutError/DialogCheckoutNotify'
import { InvoiceCategory } from './components/InvoiceCategory/InvoiceCategory'
import { MerchantInfo } from './components/MerchantInfo/MerchantInfo'
import { OrderNotes } from './components/OrderNotes/OrderNotes'
import { PaymentDetailsUI } from './components/PaymentDetailsUI'
import { PaymentMethod as MerchantPaymentMethod } from './components/PaymentMethod/PaymentMethod'
import { ShippingMethod } from './components/ShippingMethod/ShippingMethod'
import { StmAlipayDialog } from './StmAlipayDialog'
import { StmApplePayDialog } from './StmApplePayDialog'
import { StmCreditCardDialog } from './StmCreditCardDialog'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  formRoot: {
    backgroundColor: colors.grey100,
    minHeight: '100vh',
  },
  root: {
    padding: 20,
    gap: 20,
    backgroundColor: colors.grey100,
    display: {
      [MOBILE]: 'block',
      default: 'grid',
    },
    gridTemplateColumns: '2fr 1fr',
    paddingBottom: 80,
  },
})

export function Checkout() {
  const { openDialog: openDialogCouponException } = useDialog(DIALOG_COUPON_EXCEPTION)
  const { openDialog: openDialogValidateStock } = useDialog<DialogValidateStockProps>(DIALOG_VALIDATE_STOCK)
  const isFormInit = useRef(false)
  const { basePath, projectCode, countryCode, campaignId, campaignCode, platform } = useCheckout()
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const { merchant } = useFpB2bMerchantService(campaignId, platform)
  const { t } = useTranslation()

  const isShopManager = userInfo?.roles.includes('shop_manager')
  const { destructuredData: merchantProfile, isLoading: isMerchantProfileLoading } = useApiMerchantProfile(isShopManager ? { code: campaignCode, merchant_id: merchant?.index } : undefined)
  const { destructuredData: partnerProfile, isLoading: isPartnerProfileLoading } = useApiPartnerProfile(!isShopManager ? { code: campaignCode } : undefined)

  const { items: cartItems, resetCartValues } = useFpB2bCartService(campaignId, platform)
  const { selectedGiveaways, manualInputCouponIds, selectedRegularGiveaways, resetCouponsAndGiveaways } = useFpB2bCouponService(
    campaignId,
    projectCode,
    countryCode,
    campaignCode,
    platform,
  )

  const preOrderDeliveryDate = usePreOrderDeliveryDate({ campaignId, projectCode, countryCode, campaignCode, platform })

  const { destructuredData: blockoutDeliveryDays, isLoading: isBlockoutDeliveryDaysLoading } = useApiBlockoutDeliveryDays({
    country_code: countryCode,
    project_code: projectCode,
    platform: Platform.FASTPASS,
    pre_order_delivery_date: preOrderDeliveryDate,
  })
  const { configuredPaymentModule, selectedPayment, userPaymentTokenInfo, setIsProcessingCheckout, isCurrentCreditCardRemembered } = useContextPayment()
  const { destructuredData: merchantData, isLoading: isMerchantDataLoading } = useApiGetMerchant({
    platform: Platform.FORTUNE,
    project_code: projectCode,
    index: merchant?.index ?? undefined,
    service: 'fast_pass',
    country_code: countryCode,
    user_id: parseInt(userInfo?.platform_uid ?? ''),
  })
  const { calculateResult: campaignCouponCalculateResult } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)

  const { openDialog: openDialogCheckoutError } = useDialog(DIALOG_CHECKOUT_NOTIFY)
  const { trigger: createPaymentTransaction } = useApiFpCretePaymentTransaction({ campaign_code: campaignCode })
  const { calculateResult } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const [isProcessing, setIsProcessing] = useState(false)
  const { appliedShippingMethod } = useFpB2bShippingService(campaignId, campaignCode, platform)
  const { setPaymentTransactionStatus, setPaymentTransactionId, checkIfNeedRedirectToOrderPayPage, resetPaymentTransaction, paymentTransactionId, payMePaymentQRCodeUrl, setPayMePaymentQRCodeUrl } = useFpB2bPaymentService({ campaignId, paymentTransactionIdFromUrl: undefined, campaignCode, platform })
  const merchantFormData = isShopManager ? merchantProfile : partnerProfile
  const { reset: checkoutOrderReset, setError: checkoutOrderSetError, clearErrors: checkoutOrderClearErrors, setValue, getValues } = useFormContext<CheckoutOrder>()
  const formValues = useWatch<CheckoutOrder>()
  const { destructuredData: activityInfo } = useApiCampaignActivityInfoImmutable(campaignCode)
  const { openDialog: openRedirectConfirmDialog, closeDialog: closeDialogRedirectConfirm } = useDialog<DialogRedirectConfirmProps>(DIALOG_REDIRECT_CONFIRM)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const queryType = searchParams.get('type')
  const history = useHistory()
  const ga4ClientIdStore = useGa4ClientId()

  const handleDialogDisplay = useCallbackEvent(() => {
    openRedirectConfirmDialog({
      title: '訂單未完成結帳',
      description: '尚有待付款訂單，是否前往結帳？',
      confirmText: '前往結帳',
      cancelText: '返回首頁',
      onConfirm: () => {
        closeDialogRedirectConfirm()
        resetCartValues()
        resetPaymentTransaction()
        history.push(`${basePath}/my-account/order?tabValue=pending`)
      },
      onCancel: () => {
        closeDialogRedirectConfirm()
        resetCartValues()
        resetPaymentTransaction()
        history.push(`${basePath}/product-catalog`)
      },
    })
  })
  useEffectOnce(() => {
    checkIfNeedRedirectToOrderPayPage(handleDialogDisplay)
  })

  useEffect(() => {
    if (queryType !== 'failedPayment') return
    handleDialogDisplay()
  }, [handleDialogDisplay, openRedirectConfirmDialog, queryType, resetCartValues, resetPaymentTransaction])

  // 彈窗取消／關閉時，訂單已建成待付款：導去該筆的代付款頁重付，避免停在結帳頁再送出建出重複訂單
  const promptRedirectToRetryPayment = useCallbackEvent((transactionId: string) => {
    openRedirectConfirmDialog({
      title: '訂單未完成結帳',
      description: '尚有待付款訂單，是否前往付款？',
      confirmText: '前往付款',
      cancelText: '返回首頁',
      onConfirm: () => {
        closeDialogRedirectConfirm()
        resetCartValues({ excludes: ['butler'] })
        history.push(`${basePath}/retry-payment/${transactionId}`)
      },
      onCancel: () => {
        closeDialogRedirectConfirm()
        resetCartValues({ excludes: ['butler'] })
        history.push(`${basePath}/product-catalog`)
      },
    })
  })

  const handleCouponException = useCallbackEvent((code?: CouponExceptionCodeType, detail?: CartValidationDetail) => {
    const errorMessage = getCouponExceptionMessage(code, detail)
    openDialogCouponException({
      message: t(errorMessage.message, { ...errorMessage?.value, defaultValue: errorMessage.message }),
      onConfirm: () => {
        resetCouponsAndGiveaways()
        history.push(`${basePath}/cart`)
      },
    })
    datadogRum.addError(new Error(`Coupon Exception code=${code}`), {
      function: `[handleCouponException] ${detail?.message}`,
      detail,
    })
  })

  const handleUnexpectedException = useCallbackEvent((error: unknown) => {
    const ckoErrorInfo = getCkoPaymentErrorInfo(error)
    if (ckoErrorInfo) {
      datadogRum.addError(error, {
        function: '[processPayment] CKO 付款錯誤',
        ckoErrorCode: ckoErrorInfo.code,
        ckoErrorType: ckoErrorInfo.type,
        ckoDeclineReason: ckoErrorInfo.declineReason,
        ckoErrorKind: ckoErrorInfo.kind,
      })
    } else {
      datadogRum.addError(error, {
        function: '[processPayment] 非預期的 JS 錯誤：結帳失敗',
        error,
      })
    }
  })

  const handleCustomerApiException: UseHandleCheckoutExceptionArgs['handleCustomerApiException'] = useCallbackEvent((error) => {
    const message = error.response?.data.error.message || '結帳失敗'
    datadogRum.addError(error, {
      function: '[processPayment] 結帳失敗',
      message,
    })
  })

  const handleTheOthersApiException: UseHandleCheckoutExceptionArgs['handleTheOthersApiException'] = useCallbackEvent((error) => {
    datadogRum.addError(error, {
      function: '[processPayment] 結帳失敗',
    })
  })

  const { handleCheckoutException } = useHandleCheckoutException({
    handleCouponException,
    handleCustomerApiException,
    handleTheOthersApiException,
    handleUnexpectedException,
  })

  // 結帳失敗統一呈現：跳「結帳錯誤」dialog + 既有 exception 處理。信用卡與一般結帳共用。
  const showCheckoutError = useCallbackEvent((error: unknown, onConfirm?: () => void) => {
    const ckoErrorInfo = getCkoPaymentErrorInfo(error)
    if (ckoErrorInfo) {
      openDialogCheckoutError({ title: '結帳錯誤', content: CKO_ERROR_MESSAGE[ckoErrorInfo.kind], onConfirm })
    } else {
      const errorData = (error as AxiosError<ResponseErrorGenericsV2<Record<string, string[]>[] | string[]>>).response?.data.error
      const content = errorData?.detail ? getErrorContent(errorData.detail) : errorData?.message ?? '結帳失敗'
      openDialogCheckoutError({ title: '結帳錯誤', content, onConfirm })
    }
    handleCheckoutException(error)
  })

  const buildSuccessUrl = (id: string) => `${window.location.origin}${basePath}/thankyou/${id}`
  const buildFallbackUrl = () => `${window.location.origin}${basePath}/checkout?type=failedPayment`

  const handlePaymentPrimeError = useCallbackEvent((error: unknown) => {
    if (error instanceof PaymentError) {
      datadogRum.addError(error, {
        function: '[getPaymentModulePrime] Payment module error',
        service: error.service,
        code: error.code,
        detail: error.detail,
      })
    }
    else {
      datadogRum.addError(error, {
        function: '[getPaymentModulePrime] Unexpected error',
      })
    }
    openDialogCheckoutError({ title: '結帳錯誤', content: '付款人資料錯誤，請修正後再試' })
  })

  const {
    dispatch,
    applePayDialogContext,
    closeApplePayDialog,
    alipayDialogContext,
    closeAlipayDialog,
    creditCardDialogContext,
    closeCreditCardDialog,
  } = useFpB2bPaymentDispatch({
    paymentModule: configuredPaymentModule,
    urlBuilders: { buildSuccessUrl, buildFallbackUrl },
    onPaymentPrimeError: handlePaymentPrimeError,
  })

  const handleExecutePaymentProcess = async (paymentTransactionId: string, orderHash?: string): Promise<string | undefined> => {
    if (!selectedPayment?.id) {
      throw new Error('[handleExecutePaymentProcess] selectedPayment.id is required')
    }
    // CKO 彈窗路線（信用卡 / AliPayHK）dispatch 回 undefined、交棒給 dialog；此處不需再處理 redirect
    const paymentResult = await dispatch({
      paymentTransactionId,
      paymentOptionId: selectedPayment?.id,
      orderHash,
      email: formValues.billing_address_email,
      bankCode: formValues.bank_code,
      userPaymentTokenInfo,
      rememberPaymentCardToken: isCurrentCreditCardRemembered,
    })
    if (!paymentResult || !('redirect_url' in paymentResult)) {
      return undefined
    }
    if (isPayMeUrl(paymentResult.redirect_url)) {
      setPayMePaymentQRCodeUrl(paymentResult.redirect_url)
      return paymentResult.redirect_url
    }
    window.location.href = paymentResult.redirect_url
    return undefined
  }

  const processCheckout = async (data: CheckoutOrder) => {
    let hasError = false
    const isCompleteAddressRequired = formValues.billing_country && [CountryCode.TW, CountryCode.HK].includes(formValues.billing_country)
    const isValidAddress = !isCompleteAddressRequired || (formValues.billing_address_city && formValues.billing_address_state)

    if (!formValues.billing_address_first_name || !formValues.billing_address_email || !formValues.billing_address_phone) {
      checkoutOrderSetError('merchantInfoValidation', { message: '此為必填欄位' })
      hasError = true
    }
    else {
      checkoutOrderClearErrors('merchantInfoValidation')
    }
    if (!formValues.billing_address_address1 || !isValidAddress) {
      checkoutOrderSetError('shippingMethodValidation', { message: '此為必填欄位' })
      hasError = true
    }
    else {
      checkoutOrderClearErrors('shippingMethodValidation')
    }
    if (hasError) return

    if (!calculateResult?.applied_coupons) {
      throw new Error('[processCheckout] Missing applied_coupons')
    }
    if (!appliedShippingMethod?.shipping_method_id) {
      throw new Error('[processCheckout] Missing shipping_method_id')
    }
    if (!campaignCode) {
      throw new Error('[processCheckout] Missing campaign_code')
    }

    try {
      const appliedCouponIds = calculateResult?.applied_coupons?.filter(
        coupon => coupon.criterions.some(criterion => criterion.applied_status === AppliedStatus.APPLIED),
      ).map(coupon => coupon.coupon_id) || []
      const { data: paymentTransaction } = await createPaymentTransaction({
        order_items: data.order_items.map(item => ({
          project_code: item.project_code,
          sku: item.sku,
          quantity: item.quantity,
          subtotal: item.subtotal,
          is_addon: !!item.is_addon,
        })),
        merchant_id: merchant.index,
        manual_input_coupon_ids: data.manual_input_coupon_ids,
        applied_shipping_method_id: appliedShippingMethod?.shipping_method_id,
        applied_coupon_ids: appliedCouponIds,
        selected_coupon_giveaways: data.selected_coupon_giveaways,
        billing_country: data.billing_country,
        billing_address_first_name: data.billing_address_first_name,
        billing_address_phone: data.billing_address_phone,
        billing_address_email: data.billing_address_email,
        billing_address_postcode: data.billing_address_postcode,
        billing_address_state: data.billing_address_state,
        billing_address_city: data.billing_address_city,
        billing_address_address1: data.billing_address_address1,
        billing_address_address2: data.billing_address_address2,
        customer_note: data.customer_note,
        delivery_date: format(new Date(data.delivery_date), DATE_FORMAT),
        delivery_time_slot: data.delivery_time_slot,
        refund_statement: data.refund_statement,
        receipt_type: data.receipt_type as unknown as ReceiptType,
        ...resolveInvoiceDetailFields(data),
        posting_date_in_next_month: data.posting_date_in_next_month,
        is_priority_shipping: data.is_priority_shipping,
        is_specific_process: data.is_specific_process,
        need_purchase_order: data.need_purchase_order,
        need_order_pdf: data.need_order_pdf,
        headers: ga4ClientIdStore.current
          ? {
            'X-Ga-Client-Id': ga4ClientIdStore.current,
          }
          : undefined,
      })
      if (!('data' in paymentTransaction)) {
        throw new Error(`createPaymentTransaction error ${JSON.stringify(paymentTransaction)}`)
      }
      setPaymentTransactionId(paymentTransaction.data.id)
      setPaymentTransactionStatus(paymentTransaction.data.status)
      // 建單後統一交給 dispatch：信用卡 / AliPayHK 開對應彈窗，token 直接送出
      await handleExecutePaymentProcess(paymentTransaction.data.id, paymentTransaction.data.order_hash)
    }
    catch (error) {
      const serverError = (error as AxiosError<ResponseErrorGenericsV2<CartValidationDetail>>).response?.data.error
      if (serverError?.code === StockExceptionCode.OUT_OF_STOCK) {
        openDialogValidateStock({
          productName: serverError.detail?.product_name,
          leftQuantity: serverError.detail?.left_quantity,
          onConfirm: () => history.push(`${basePath}/cart`),
        })
        datadogRum.addError(new Error(`Stock Exception code=${serverError.code}`), {
          function: '[processPayment] 庫存不足',
        })
        return
      }
      showCheckoutError(error)
    }
  }

  const handleSubmit = (data: CheckoutOrder) => {
    setIsProcessing(true)
    setIsProcessingCheckout(true)
    processCheckout(data).finally(() => {
      setIsProcessing(false)
      setIsProcessingCheckout(false)
    })
  }

  useEffect(() => {
    if (isMerchantProfileLoading || isMerchantDataLoading || isPartnerProfileLoading || isBlockoutDeliveryDaysLoading || isFormInit.current) {
      return
    }
    checkoutOrderReset({
      billing_address_address1: merchantFormData?.billing_address_address1 || '',
      billing_address_address2: '',
      billing_address_city: merchantFormData?.billing_address_city || '',
      billing_address_email: merchantFormData?.billing_address_email || '',
      billing_address_first_name: merchantFormData?.billing_address_first_name || '',
      billing_address_phone: merchantFormData?.billing_address_phone || '',
      billing_address_postcode: merchantFormData?.billing_address_postcode || '',
      billing_address_state: merchantFormData?.billing_address_state || '',
      billing_country: merchantFormData?.billing_country || activityInfo?.region,
      business_ubn_title: merchantFormData?.business_ubn_title || '',
      business_ubn: merchantFormData?.business_ubn || '',
      charity_organization: '',
      customer_note: isShopManager ? merchantFormData?.customer_note : undefined,
      delivery_date: blockoutDeliveryDays?.default_delivery_date
        ? fromUnixTime(blockoutDeliveryDays.default_delivery_date).toISOString()
        : '',
      delivery_time_slot: isShopManager ? (merchantFormData?.delivery_time_slot ?? DeliveryTimeSlot.MORNING) : undefined,
      manual_input_coupon_ids: manualInputCouponIds,
      need_order_pdf: !!merchantFormData?.need_pdf,
      order_items: cartItems.filter(item => !item.butler?.isDeleted),
      phone_carrier_number: merchantFormData?.phone_carrier_number || '',
      posting_date_in_next_month: false,
      receipt_type: merchantFormData?.receipt_type,
      refund_statement: true,
      selected_coupon_giveaways: [...selectedRegularGiveaways, ...selectedGiveaways],
      is_priority_shipping: !!merchantData?.is_priority_shipping,
      is_specific_process: !!merchantData?.is_specific_process,
      need_purchase_order: !!merchantData?.need_purchase_order,
    })
    isFormInit.current = true
  }, [
    activityInfo,
    blockoutDeliveryDays,
    cartItems,
    checkoutOrderReset,
    isBlockoutDeliveryDaysLoading,
    isMerchantProfileLoading,
    isMerchantDataLoading,
    isPartnerProfileLoading,
    isShopManager,
    manualInputCouponIds,
    merchantData,
    merchantFormData,
    selectedGiveaways,
    selectedRegularGiveaways,
  ])

  const { startPolling, stopPolling } = useFpB2bPaymentTransactionPolling({
    paymentTransactionId,
    basePath: basePath ?? '',
  })
  useEffect(() => {
    /** Poll for transaction state of QR code payment */
    if (!payMePaymentQRCodeUrl) return
    startPolling()
    return () => stopPolling()
  }, [payMePaymentQRCodeUrl, startPolling, stopPolling])

  useEffect(() => {
    /**
     * 當購物車有變動時，同步更新 form 的 order_items
     * 情境：使用者開兩個以上 tab，操作購物車，導致 form 的 order_items 與購物車的 order_items 不同步
    */
    const syncOrderItemsWithCart = () => {
      if (!isFormInit.current) return

      const currentCartItems = cartItems.filter(item => !item.butler?.isDeleted)
      const formOrderItems = getValues('order_items')

      const cartItemsKey = JSON.stringify(currentCartItems.map(item => ({ sku: item.sku, quantity: item.quantity })))
      const formItemsKey = JSON.stringify(formOrderItems?.map(item => ({ sku: item.sku, quantity: item.quantity })))

      const hasCartChanged = cartItemsKey !== formItemsKey
      if (!hasCartChanged) return

      const orderItems = currentCartItems.map(item => ({
        ...item,
        subtotal: (item.sale_price ?? 0) * (item.quantity ?? 0),
        is_addon: !!item.is_addon,
      }))
      setValue('order_items', orderItems)
    }
    syncOrderItemsWithCart()
  }, [cartItems, getValues, setValue])

  if (isMerchantProfileLoading || !activityInfo?.region) return <LoaderFullPage />
  const shouldShowInvoiceCategory = formValues.billing_country && ![CountryCode.HK, CountryCode.MO].includes(formValues.billing_country)

  return (
    <>
      <Box rootStyle={styles.formRoot}>
        <Container rootStyle={styles.root}>
          <VStack gap={5}>
            <MerchantInfo />
            <ShippingMethod deliveryClass={cartItems[0]?.delivery_class || DELIVERY_CLASS.NORMAL} />
            <MerchantPaymentMethod />
            <OrderNotes />
            {shouldShowInvoiceCategory && <InvoiceCategory />}
            <DeliveryNotes />
          </VStack>
          <PaymentDetailsUI
            currency={activityInfo.currency || '(幣別未知)'}
            countryCode={activityInfo.region}
            discountItems={campaignCouponCalculateResult?.discount_items}
            shippingTotal={campaignCouponCalculateResult?.shipping_total}
            total={campaignCouponCalculateResult?.total}
            subtotal={campaignCouponCalculateResult?.subtotal}
            subtotalAfterDiscount={campaignCouponCalculateResult?.total_without_addon_v2 || 0}
            tax={campaignCouponCalculateResult?.tax}
            addonV2Total={campaignCouponCalculateResult?.addon_v2_total}
            addonV2TotalBeforeDiscount={campaignCouponCalculateResult?.addon_v2_total_before_discount}
          />
        </Container>
        <PageFooter detail={<CartFooterDetail />} actionButton={<CheckoutFooterAction onSubmit={handleSubmit} isLoading={isProcessing} />} />
        <DialogCouponException />
        <DialogRedirectConfirm />
        <DialogCheckoutNotify />
        <DialogValidateStock />
      </Box>
      {!!payMePaymentQRCodeUrl && (
        <DialogPayMeQRCode
          url={payMePaymentQRCodeUrl}
          onRefresh={paymentTransactionId ? () => handleExecutePaymentProcess(String(paymentTransactionId)) : undefined}
        />
      )}
      {applePayDialogContext && configuredPaymentModule instanceof CheckoutPayApplePayModule && (
        <StmApplePayDialog
          context={applePayDialogContext}
          paymentModule={configuredPaymentModule}
          countryCode={countryCode}
          onClose={() => {
            closeApplePayDialog()
            promptRedirectToRetryPayment(String(applePayDialogContext.paymentTransactionId))
          }}
          onError={(error) => {
            closeApplePayDialog()
            showCheckoutError(error)
          }}
        />
      )}
      {creditCardDialogContext && configuredPaymentModule instanceof CheckoutPayCreditCardModule && (
        <StmCreditCardDialog
          context={creditCardDialogContext}
          paymentModule={configuredPaymentModule}
          countryCode={countryCode}
          onClose={() => {
            closeCreditCardDialog()
            promptRedirectToRetryPayment(String(creditCardDialogContext.paymentTransactionId))
          }}
          onError={(error) => {
            closeCreditCardDialog()
            showCheckoutError(error, () => promptRedirectToRetryPayment(String(creditCardDialogContext.paymentTransactionId)))
          }}
        />
      )}
      {alipayDialogContext && configuredPaymentModule instanceof CheckoutPayAlipayHkModule && (
        <StmAlipayDialog
          context={alipayDialogContext}
          paymentModule={configuredPaymentModule}
          countryCode={countryCode}
          onCancelClose={closeAlipayDialog}
          onErrorClose={() => {
            closeAlipayDialog()
            openDialogCheckoutError({ title: '結帳錯誤', content: '付款發生問題，請稍後再試或選擇其他付款方式。' })
          }}
        />
      )}
    </>
  )
}
// TODO: 將舊的預設付款方式改為新方式
export const paymentConversionMap: Partial<Record<
  PaymentMethod,
  { serviceType: Nullable<PaymentServiceType>, methodType: PaymentMethodType }
>> = {
  [PaymentMethod.TAPPAY_DIRECT]: {
    serviceType: PaymentServiceType.TAPPAY,
    methodType: PaymentMethodType.CREDIT_CARD_ONCE,
  },
  [PaymentMethod.CHECKOUTPAY_DIRECT]: {
    serviceType: PaymentServiceType.CHECKOUTPAY,
    methodType: PaymentMethodType.CREDIT_CARD_ONCE,
  },
  [PaymentMethod.TAPPAY_JKO_PAY]: {
    serviceType: PaymentServiceType.TAPPAY,
    methodType: PaymentMethodType.JKO_PAY,
  },
  [PaymentMethod.CHECKOUTPAY_APPLE_PAY]: {
    serviceType: PaymentServiceType.CHECKOUTPAY,
    methodType: PaymentMethodType.APPLE_PAY,
  },
  [PaymentMethod.TAPPAY_APPLE_PAY]: {
    serviceType: PaymentServiceType.TAPPAY,
    methodType: PaymentMethodType.APPLE_PAY,
  },
  [PaymentMethod.OFFLINE_LINE_PAY]: {
    serviceType: PaymentServiceType.LINEPAY,
    methodType: PaymentMethodType.LINE_PAY,
  },
  [PaymentMethod.COD]: {
    serviceType: null,
    methodType: PaymentMethodType.CASH_ON_DELIVERY,
  },
  [PaymentMethod.BACS]: {
    serviceType: null,
    methodType: PaymentMethodType.ASSIGNED_PAYMENT_INVOICE,
  },
  [PaymentMethod.CHEQUE]: {
    serviceType: null,
    methodType: PaymentMethodType.ASSIGNED_PAYMENT,
  },
}

export const getDefaultPaymentMethod = (
  paymentMethods: PaymentMethodTypeECommerce[],
  defaultCampaignPayment: { methodType: PaymentMethodType | undefined, serviceType: PaymentServiceType | undefined },
  /** 經銷商在簡道雲上的付款設定 */
  merchantSettingPayment?: PaymentMethod,
): PaymentMethodTypeECommerce | null => {
  const merchantEcPayment = merchantSettingPayment && paymentConversionMap[merchantSettingPayment]
    ? paymentConversionMap[merchantSettingPayment]
    : null
  const defaultValue = merchantEcPayment || defaultCampaignPayment

  return paymentMethods.find((payment) => {
    const isSameService = payment.service_type === defaultValue.serviceType
    const isSameType = payment.type === defaultValue.methodType
    return payment.service_type ? (isSameService && isSameType) : isSameType
  }) || paymentMethods[0] || null
}

export function CheckoutProcessor() {
  const { campaignId, campaignCode, platform } = useCheckout()
  const { calculateResult: calculation, defaultCampaignPayment } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const { merchant } = useFpB2bMerchantService(campaignId, platform)
  const isShopManager = userInfo?.roles.includes('shop_manager')
  const { destructuredData: merchantProfile, isLoading: isLoadingMerchantProfile } = useApiMerchantProfile(isShopManager ? { code: campaignCode, merchant_id: merchant?.index } : undefined)

  const mappedDefaultPayment = getDefaultPaymentMethod(
    calculation?.payment_methods || [],
    defaultCampaignPayment,
    merchantProfile?.payment_method,
  )
  const form = useForm<CheckoutOrder>({
    defaultValues: {},
    mode: 'onChange',
  })
  if (isLoadingMerchantProfile) return <LoaderFullPage />
  return (
    <PaymentProvider paymentMethods={calculation?.payment_methods} defaultPaymentMethod={mappedDefaultPayment || undefined}>
      <FormProvider {...form}>
        <Checkout />
      </FormProvider>
    </PaymentProvider>
  )
}
